import { useEffect, useState } from 'react';
import type { WorkbookRun, WorkbookRunTiming } from '@erp/shared-types';
import { formatDurationMs } from '@/utils/formatDurationMs';

export type WorkbookRunStep = NonNullable<WorkbookRunTiming['step']>;

/** The step the newest running Run is on; undefined while none runs. */
export function activeRunStep(
  runs: readonly WorkbookRun[] | undefined,
): WorkbookRunStep | undefined {
  return (
    runs?.find((run) => run.status === 'running')?.timing?.step ?? undefined
  );
}

/** Milliseconds since `startedAt`, ticking every second; undefined without one. */
export function useElapsed(startedAt: string | undefined): number | undefined {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);
  if (!startedAt) return undefined;
  // A server clock ahead of this one reads as just started, never negative.
  return Math.max(0, now - Date.parse(startedAt));
}

/** A running time: whole seconds under a minute ("4s"), else as {@link formatDurationMs}. */
export function formatElapsed(ms: number): string {
  return ms < 60_000
    ? `${Math.floor(ms / 1000)}s`
    : (formatDurationMs(ms) ?? '');
}

/** A running time in its largest whole unit ("4s", "2m", "1h"), for a narrow gutter. */
export function formatShortElapsed(ms: number): string {
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  return `${Math.floor(ms / 3_600_000)}h`;
}

/** A recorded time: tenths of a second under ten seconds ("0.4s"), else as {@link formatDurationMs}. */
export function formatRunTime(ms: number): string {
  return ms < 10_000
    ? `${(ms / 1000).toFixed(1)}s`
    : (formatDurationMs(ms) ?? '');
}

/** A Run's Worksheet times by name, slowest first, with their total. */
export function runSheetTimes(
  run: Pick<WorkbookRun, 'timing'>,
  sheetName: (sheetId: string) => string,
):
  | {
      rows: { sheetId: string; name: string; duration: string }[];
      total: string;
    }
  | undefined {
  const sheets = run.timing?.sheets;
  if (!sheets?.length) return undefined;
  const total = sheets.reduce((sum, sheet) => sum + sheet.durationMs, 0);
  return {
    rows: sheets
      .toSorted((a, b) => b.durationMs - a.durationMs)
      .map((sheet) => ({
        sheetId: sheet.sheetId,
        name: sheetName(sheet.sheetId),
        duration: formatRunTime(sheet.durationMs),
      })),
    total: formatRunTime(total),
  };
}
